import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { webSocketUrl } from '../config/apiBase';
import type { ConnectionStatus, WebSocketMessage, WebSocketContextType } from '../types/websocket';

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

// Reconnection settings - exponential backoff, never give up
const INITIAL_RECONNECT_DELAY = 1000; // Start at 1 second
const MAX_RECONNECT_DELAY = 300000; // Cap at 5 minutes

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const currentDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasConnectedRef = useRef(false);
  // subscribersRef is the source of truth for which channels we want subscribed.
  // The WS connection mirrors this set: on (re)connect we send subscribe for every key.
  const subscribersRef = useRef<Map<string, Set<(payload: any) => void>>>(new Map());

  // Refs to avoid stale closures in onopen/onmessage handlers
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  const lastRefreshRef = useRef(0);

  // WS URL comes from the page's own origin, not from the API base URL.
  // Django is reached same-origin through the nginx proxy, so /ws/streams/
  // resolves against whichever hostname served this bundle. Deriving it from
  // the API base used to yield "ws:///ws/streams/" once that base went
  // relative — empty host plus the insecure scheme on an HTTPS page.
  //
  // Auth rides the HttpOnly session cookie on the WS handshake (asgi.py's
  // AuthMiddlewareStack), which is exactly why the WS must traverse the same
  // proxy as REST: a cross-apex connection would carry no session cookie and
  // scope["user"] would be AnonymousUser.
  const getWebSocketUrl = useCallback(() => webSocketUrl('/ws/streams/'), []);

  // Send raw message to WebSocket. Used for non-subscription actions (e.g. ping).
  // Do NOT use this for {action: 'subscribe'/'unsubscribe'} — call subscribe() instead.
  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }, []);

  // Subscribe to a channel with ref-counted server subscribe/unsubscribe.
  //
  // The first local subscriber for a channel triggers a server-side subscribe.
  // The last local subscriber leaving triggers a server-side unsubscribe.
  // If the WS isn't open, the channel is still recorded locally and will be
  // re-subscribed automatically on reconnect (see ws.onopen).
  const subscribe = useCallback((channel: string, callback: (payload: any) => void) => {
    let subs = subscribersRef.current.get(channel);
    const isFirst = !subs;
    if (!subs) {
      subs = new Set();
      subscribersRef.current.set(channel, subs);
    }
    subs.add(callback);

    if (isFirst && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'subscribe', channel }));
    }

    return () => {
      const current = subscribersRef.current.get(channel);
      if (!current) return;
      current.delete(callback);
      if (current.size === 0) {
        subscribersRef.current.delete(channel);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'unsubscribe', channel }));
        }
      }
    };
  }, []);

  // Stable handler for user update messages (registered once, uses refs)
  const userUpdateHandler = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshRef.current > 1000) {
      lastRefreshRef.current = now;
      refreshUserRef.current();
    }
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);

      // For channel-specific messages, route by channel
      if (message.type === 'message' && message.channel) {
        const subscribers = subscribersRef.current.get(message.channel);
        if (subscribers) {
          subscribers.forEach((callback) => callback(message));
        }
      } else if (message.type) {
        // For other message types (pong, error), route by type
        const subscribers = subscribersRef.current.get(message.type);
        if (subscribers) {
          subscribers.forEach((callback) => callback(message.payload));
        }
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!isAuthenticated) {
      console.log('Not authenticated, skipping WebSocket connection');
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    try {
      setConnectionStatus('connecting');
      // Auth rides the HttpOnly session cookie on the WS handshake; no ?token=.
      const wsUrl = getWebSocketUrl();
      console.log('Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnectionStatus('connected');
        currentDelayRef.current = INITIAL_RECONNECT_DELAY;

        // Re-subscribe to every channel we have local subscribers for.
        // This handles both the initial connect (channels added before WS opened)
        // and reconnects (server-side subscriptions were dropped on disconnect).
        for (const channel of subscribersRef.current.keys()) {
          ws.send(JSON.stringify({ action: 'subscribe', channel }));
        }

        // On reconnect, backfill all cached data and refresh user permissions
        if (hasConnectedRef.current) {
          console.log('WebSocket reconnected — invalidating queries and refreshing user');
          // Active observers only. A blanket invalidate refetches every ticker
          // and page the user visited this session and spikes RAM on reconnect.
          queryClient.invalidateQueries({ type: 'active' });
          refreshUserRef.current();
        }
        hasConnectedRef.current = true;
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnectionStatus('disconnected');

        // Always schedule reconnect (never give up)
        const delay = currentDelayRef.current;
        console.log(`Reconnecting in ${delay / 1000}s...`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);

        // Exponential backoff: double the delay, capped at max
        currentDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('disconnected');
    }
  }, [isAuthenticated, getWebSocketUrl, handleMessage, queryClient]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionStatus('disconnected');
    hasConnectedRef.current = false;
    currentDelayRef.current = INITIAL_RECONNECT_DELAY;
  }, []);

  // Connect on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, connect, disconnect]);

  // Subscribe to per-user update channel for real-time permission pushes.
  // subscribe() handles the WS-not-yet-open case and reconnects automatically.
  useEffect(() => {
    if (!user?.id) return;
    const channel = `user.${user.id}.updated`;
    return subscribe(channel, userUpdateHandler);
  }, [user?.id, userUpdateHandler, subscribe]);

  // Reconnect immediately when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          // Cancel any pending long-delay reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          currentDelayRef.current = INITIAL_RECONNECT_DELAY;
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, connect]);

  const value: WebSocketContextType = {
    connectionStatus,
    sendMessage,
    subscribe,
    isConnected: connectionStatus === 'connected',
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

// Custom hook to use WebSocket context
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
